import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Link } from 'react-router-dom';
import { getAllCaseStudiesMeta } from '../lib/casestudies';

export function Home() {
  // Get the last 3 case studies
  const allCaseStudies = getAllCaseStudiesMeta();
  const recentCaseStudies = allCaseStudies.slice(-3).reverse();

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="relative overflow-hidden py-12 md:py-16 lg:min-h-screen lg:flex lg:items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-text-dark leading-tight px-4">
              We are the Technical Team Behind your startup (incl. CTO)
            </h1>
          </div>
          <div className="relative mb-8 md:mb-12">
            <img
              src="/assets/team-photo-dfb608c484c0aa34478b9045a37c5c9d.webp"
              alt="Team"
              className="w-full rounded-lg shadow-xl"
            />
          </div>
          <div className="text-center max-w-4xl mx-auto px-4">
            <p className="text-base md:text-lg lg:text-xl text-text-gray">
              Our developers logged over 6800 developer hours and hit dozens of deadlines. Learn more in our <Link to="/casestudies" className="text-primary-blue hover:underline">case studies</Link>
            </p>
          </div>
        </div>
      </section>

      {/* Trusted By Section */}
      <section className="py-12 bg-bg-gray-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-text-dark mb-8">
            Trusted by Great Companies
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 items-center justify-items-center opacity-70">
            <img src="/assets/logo1-9f7722086ff6498a15f5a2693f50d215.svg" alt="Company" className="h-12 w-auto" />
            <img src="/assets/logo2-96c73d9676e7dfc3c9b38293d1cb53fb.svg" alt="Company" className="h-12 w-auto" />
            <img src="/assets/logo3-f5654799e995c94a5ccdfa10dbdcb4f9.svg" alt="Company" className="h-12 w-auto" />
            <img src="/assets/logo5-35b8cca583fc44ff40d46482d5871ea3.svg" alt="Company" className="h-12 w-auto" />
            <img src="/assets/logo6-b8fd89388160baecd1ceeecedda9bc53.svg" alt="Company" className="h-12 w-auto" />
          </div>
        </div>
      </section>

      {/* Results/Stats Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-text-dark mb-12">
            The results our clients got from 6800+ logged hours through Pairing
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="text-4xl md:text-5xl font-bold text-primary-blue mb-2">6800+</div>
              <div className="text-lg text-text-gray">Developer Hours Logged</div>
            </div>
            <div className="text-center p-6">
              <div className="text-4xl md:text-5xl font-bold text-primary-blue mb-2">Dozens</div>
              <div className="text-lg text-text-gray">Deadlines Hit</div>
            </div>
            <div className="text-center p-6">
              <div className="text-4xl md:text-5xl font-bold text-primary-blue mb-2">100%</div>
              <div className="text-lg text-text-gray">Client Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-16 bg-bg-gray">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <blockquote className="text-center">
            <p className="text-2xl md:text-3xl font-medium text-text-dark italic">
              "Just one Pairing team improved our bottom line by 6%. This sounds small but it's actually a big deal!"
            </p>
          </blockquote>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-text-dark mb-4">
            Case Studies
          </h2>
          <p className="text-center text-lg text-text-gray mb-12">
            We worked with startups, marketing agencies, insurance, medical. <Link to="/casestudies" className="text-primary-blue hover:underline">See all case studies</Link>.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {recentCaseStudies.map((caseStudy) => (
              <Link
                key={caseStudy.slug}
                to={`/casestudies/${caseStudy.slug}`}
                className="bg-white rounded-lg border border-border-gray overflow-hidden hover:shadow-lg transition-shadow"
              >
                <img
                  src={caseStudy.image}
                  alt={caseStudy.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-text-dark mb-2">{caseStudy.title}</h3>
                  <p className="text-text-gray">{caseStudy.subtitle}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-white via-blue-100 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-text-dark mb-4">
            Our Lean Process
          </h2>
          <p className="text-center text-lg text-text-gray mb-16">
            Our clients experience these changes in how they work with software developers
          </p>
          <div className="grid md:grid-cols-3 gap-12">
            <div className="bg-white rounded-lg p-8 text-center hover:shadow-xl transition-shadow">
              <div className="mb-6">
                <img
                  src="/assets/benefit-1-2c236750b151384f4ebe403b5ecd2da2.webp"
                  alt="Trust Estimations"
                  className="w-full h-64 object-contain rounded-lg"
                />
              </div>
              <h3 className="text-xl font-semibold text-text-dark mb-2">
                Trust Estimations again
              </h3>
            </div>
            <div className="bg-white rounded-lg p-8 text-center hover:shadow-xl transition-shadow">
              <div className="mb-6">
                <img
                  src="/assets/benefit-2-5dccabe277117e06bac695496fe4ae72.webp"
                  alt="Better Shipping"
                  className="w-full h-64 object-contain rounded-lg"
                />
              </div>
              <h3 className="text-xl font-semibold text-text-dark mb-2">
                Feel better about what you ship
              </h3>
            </div>
            <div className="bg-white rounded-lg p-8 text-center hover:shadow-xl transition-shadow">
              <div className="mb-6">
                <img
                  src="/assets/benefit-3-0572d7ef2fff25c9f5ab6b718e41db03.webp"
                  alt="Cost Reduction"
                  className="w-full h-64 object-contain rounded-lg"
                />
              </div>
              <h3 className="text-xl font-semibold text-text-dark mb-2">
                Decrease the cost of expensive consultants
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-text-dark mb-4">
            Features
          </h2>
          <p className="text-center text-lg text-text-gray mb-12">
            You will receive these things along the way to your software success
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-bg-gray-light rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="mb-6">
                <img
                  src="/assets/feature1-232d1c52339b148825a3b24747cb864b.webp"
                  alt="Action Plan"
                  className="w-full h-64 object-contain rounded-lg"
                />
              </div>
              <h3 className="text-xl font-semibold text-text-dark mb-3">
                A clear action plan
              </h3>
              <p className="text-text-gray">
                You get a clear action plan and estimation after the first month working with TechLead. + Weekly updates.
              </p>
            </div>
            <div className="bg-bg-gray-light rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="mb-6">
                <img
                  src="/assets/feature2-c81142f8150503a2050b32dda742e7cd.webp"
                  alt="Developer Progress"
                  className="w-full h-64 object-contain rounded-lg"
                />
              </div>
              <h3 className="text-xl font-semibold text-text-dark mb-3">
                Developers show progress
              </h3>
              <p className="text-text-gray">
                Developers that will work on your product and will give you the daily check-ins, and the progress on the action plan we have set in place.
              </p>
            </div>
            <div className="bg-bg-gray-light rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="mb-6">
                <img
                  src="/assets/feature3-6bd38fb30258d250c72dba03769f58f6.webp"
                  alt="Quality Software"
                  className="w-full h-64 object-contain rounded-lg"
                />
              </div>
              <h3 className="text-xl font-semibold text-text-dark mb-3">
                Get a high quality, working software
              </h3>
              <p className="text-text-gray">
                Every time you hire a new developer you need an extensive onboarding process. We built onboarding into the trial teams.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team/About Section */}
      <section className="py-16 md:py-24 bg-bg-gray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="/assets/till-carlos-14cb0e0441904387ffe5384bf9361918.webp"
                alt="Till Carlos - Founder"
                className="rounded-lg"
              />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-text-dark mb-4">
                Founded by a German Dipl.-Inf. / Msc. Computer Science
              </h2>
              <p className="text-lg text-text-gray mb-4">
                Pairing.dev was founded in 2017 by Dipl.-Inf. Till Carlos. Coming from a freelance senior development position, Till grew the team organically without taking outside investment.
              </p>
              <p className="text-lg text-text-gray">
                Now the team consists of specialists in 10 countries. We value freedom, collaboration and industriousness.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-text-dark mb-4">
            Pricing
          </h2>
          <p className="text-center text-lg text-text-gray mb-12">
            Depending on your scope and stage of your project
          </p>
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-lg text-text-gray mb-8">
              We charge per pair day and only if the full day was worked. No hidden fees, no payment fees.
            </p>
            <Link
              to="/book"
              className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-blue hover:bg-dark-blue transition-colors"
            >
              Schedule a Call
            </Link>
          </div>
        </div>
      </section>

      {/* Tech Stack Section */}
      <section className="py-16 bg-bg-gray-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-text-dark mb-8">
            We develop Full Stack
          </h2>
          <p className="text-center text-lg text-text-gray mb-12">
            with high ROI Frameworks
          </p>
          <div className="flex justify-center items-center gap-8 flex-wrap">
            <img src="/assets/ruby-1f6d8959334238cf022a2549ef5db72e.svg" alt="Ruby" className="h-16 w-auto" />
            <img src="/assets/rails-481a5c28cea5c7da105a977a1d55f8da.svg" alt="Rails" className="h-16 w-auto" />
            <img src="/assets/typescript.svg" alt="TypeScript" className="h-16 w-auto" />
            <img src="/assets/react.svg" alt="React" className="h-16 w-auto" />
            <img src="/assets/nextjs.svg" alt="Next.js" className="h-16 w-auto" />
            <img src="/assets/fastify.svg" alt="Fastify" className="h-16 w-auto" />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-text-dark mb-12">
            FAQ
          </h2>
          <div className="space-y-6">
            {[
              {
                q: "Why buy from you? What makes you so special?",
                a: "True, you can get developers from anywhere. There are countless of local agencies and upwork is full of developers who can \"work per requirement, sir\". Pairing is a system with people behind it, that delivers results. We beat the prices of local agencies (by far) while not having the uncertainty of hiring random developers overseas."
              },
              {
                q: "What exactly am I signing up for?",
                a: "In the first step it's just a free call where we talk about your needs and what it would take to build your project. Nothing to lose here but 15 minutes of your time. Hiring developers after that is a 1-3 months commitment"
              },
              {
                q: "What happens if I wait?",
                a: "Your idea or feature will not get built. Nothing wrong with this, the market will decide if they still want your product."
              },
              {
                q: "Are there any long-term contracts?",
                a: "Only if you want a long-term discount."
              },
              {
                q: "What languages do your developers speak?",
                a: "Everyone speaks fluent English. Some speak German. And of course local languages outside of our main market."
              },
              {
                q: "How much does it cost? Are there any hidden fees?",
                a: "No hidden fees, no payment fees. We charge per pair day and only if the full day was worked."
              },
            ].map((faq, i) => (
              <details key={i} className="bg-bg-gray-light rounded-lg">
                <summary className="px-6 py-4 cursor-pointer font-semibold text-text-dark text-lg hover:text-primary-blue transition-colors">
                  {faq.q}
                </summary>
                <div className="px-6 pb-4 text-text-gray">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
